import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Avatar,
  Button,
  Tag,
  Breadcrumb,
  Spin,
  Input, 
  Modal,
 } from 'antd';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import {
  LeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import workspaceService from '../services/workspaceService';
import teamService from '../services/teamService';
import bg from '../assets/bg.png';
import WorkspaceTab from '../components/tab/WorkspaceTab';
import { getCurrentUser } from '../services/authService';
import ApplyToJoinModal from '../components/modals/ApplyToJoinModal';
import FileUploadList from '../components/common/FileUploadList';
import TagEditModal from '../components/modals/TagEditModal';
import SharePopover from '../components/common/SharePopover';
import { MISSING_ROLES_OPTIONS } from '../utils/constants';
import { hasTeams } from '../utils/workspaceHelper';
import EmptyState from '../components/common/EmptyState';
import { SHARE_QR_CONFIG, SHARE_SCENE_TYPE } from '../utils/constants';
import CreateTeamModal from '../components/modals/CreateTeamModal';

const WorkspacePage = () => {
  const { id: workspaceId } = useParams();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [editAvatar, setEditAvatar] = useState('');
  const [editAvatarFile, setEditAvatarFile] = useState(null);
  const [editAvatarPreview, setEditAvatarPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [applyModalVisible, setApplyModalVisible] = useState(false);
  const [tagEditModalVisible, setTagEditModalVisible] = useState(false);
  const [createTeamModalVisible, setCreateTeamModalVisible] = useState(false);
  const [deleteTeamModalVisible, setDeleteTeamModalVisible] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState(false);

  // 当前用户信息
  const currentUser = getCurrentUser();
  

  
  // 判断是否为自己（owner 或成员）
  const isSelf = useMemo(() => {
    return currentUser &&
      ((workspace?.owner_id && workspace.owner_id === currentUser.id) ||
        (Array.isArray(workspace?.members) &&
          workspace.members.some((m) => m.user_id === currentUser.id)));
  }, [currentUser, workspace?.owner_id, workspace?.members]);

  // 判断是否为队长（只有队长可以编辑workspace信息）
  const isCaptain = useMemo(() => {
    if (!currentUser || !workspace) return false;
    
    // 新数据结构中，owner_id 就是队长
    return workspace.owner_id === currentUser.id;
  }, [currentUser, workspace?.owner_id]);

  // 判断用户是否已有团队
  const userHasTeams = useMemo(() => {
    return hasTeams();
  }, []);

  // 编辑保存
  const handleSave = useCallback(async () => {
    if (!workspace) return;
    
    setSaving(true);
    try {
      // 检查头像是否发生变化
      const originalAvatarValue = workspace.avatar;
      const hasAvatarChanged = !!editAvatarFile || editAvatar === '';
      let avatarId = null;
      
      // 只有在头像发生变化时才处理头像
      if (hasAvatarChanged) {
        if (editAvatarFile) {
          try {
            // 优先使用上传返回的文件UUID
            if (editAvatarFile.id) {
              avatarId = editAvatarFile.id;
            } else if (editAvatarFile.originFileObj instanceof File) {
              const result = await uploadService.uploadFile(editAvatarFile.originFileObj, { alt_text: 'avatar' });
              avatarId = result.id;
            } else {
              throw new Error('头像文件无有效 id 或文件对象');
            }
          } catch (error) {
            console.error('头像上传失败:', error);
            window.message?.error('头像上传失败，但其他信息已保存');
            // 上传失败时，不更新头像
            avatarId = originalAvatarValue || null;
          }
        } else {
          // 用户清空头像
          avatarId = null;
        }
      }
      
      // 构建更新数据
      const updateData = {
        name: editName,
        display_name: editName, // 新数据结构需要 display_name
        description: editDesc,
        settings: {
          ...(workspace.settings || {}),
          tags: editTags,
        },
      };
      
      // 如果头像发生了变化，总是包含 avatar 字段（必须是UUID）
      if (hasAvatarChanged) {
        updateData.avatar = avatarId;
        console.log('头像发生变化，更新 avatar(UUID):', avatarId);
      }
      
      
      // 调用更新接口
      const result = await workspaceService.updateWorkspaceInfo(
        workspace.id,
        updateData
      );
      
      // 更新本地状态
      setWorkspace(prev => ({
        ...prev,
        name: editName,
        display_name: editName,
        description: editDesc,
        settings: {
          ...(prev.settings || {}),
          tags: editTags,
        },
        // 只有在头像发生变化时才从后端响应中获取新的头像值
        ...(hasAvatarChanged && result?.avatar && { avatar: result.avatar }),
      }));
      
      setEditMode(false);
      // 清理头像相关状态，防止下次编辑时重复传递
      setEditAvatarFile(null);
      setEditAvatarPreview(''); 
      setEditAvatar('');
      window.message?.success('保存成功');
    } catch (error) {
      console.error('保存失败:', error);
      window.message?.error('保存失败');
    } finally {
      setSaving(false);
    }
  }, [workspace, editAvatarFile, editAvatar, editName, editDesc, editTags]);

  // 进入编辑模式时初始化
  const handleEdit = useCallback(() => {
    if (!workspace) return;
    
    // 清理头像相关状态，确保每次编辑都是干净的状态
    setEditAvatarFile(null);
    setEditAvatarPreview('');
    
    setEditName(workspace.name || '');
    setEditDesc(workspace.description || '');
    setEditTags(Array.isArray(workspace.settings?.tags) && workspace.settings.tags.length > 0 ? workspace.settings.tags : []);
    setEditAvatar(workspace.avatar || '');
    setEditMode(true);
  }, [workspace]);

  // 处理申请入团
  const handleApplyToJoin = useCallback(async (message) => {
    if (!workspace) return;
    
    try {
      await teamService.submitApplication(workspace.id, {
        desired_role: 'operation_manager', // 默认为运营官
        message: message
      });
      
      setApplyModalVisible(false);
      return true;
    } catch (error) {
      console.error('申请入团失败:', error);
      throw error;
    }
  }, [workspace]);

  // 处理标签编辑
  const handleTagEdit = useCallback(async (tagData) => {
    if (!workspace) return;
    
    try {
      // 更新工作区信息，标签编辑不涉及头像，所以不传递 avatar 字段
      await workspaceService.updateWorkspaceInfo(
        workspace.id,
        {
          name: workspace.name,
          display_name: workspace.display_name,
          description: workspace.description,
          settings: {
            ...(workspace.settings || {}),
            recruiting_status: tagData.recruiting_status,
            scene: tagData.scene,
            missing_roles: tagData.missing_roles,
          },
        }
      );
      
      // 更新本地状态
      setWorkspace({
        ...workspace,
        settings: {
          ...(workspace.settings || {}),
          recruiting_status: tagData.recruiting_status,
          scene: tagData.scene,
          missing_roles: tagData.missing_roles,
        },
      });
      
      // 清理头像相关状态，防止下次编辑时重复传递
      setEditAvatarFile(null);
      setEditAvatarPreview('');
      setEditAvatar('');
      
      setTagEditModalVisible(false);
      return true;
    } catch (error) {
      console.error('标签更新失败:', error);
      throw error;
    }
  }, [workspace]);

  // 处理解散团队
  const handleDeleteTeam = useCallback(async () => {
    if (!workspace) return;
    
    setDeletingTeam(true);
    try {
      await teamService.deleteTeam(workspace.id);
      window.message?.success('团队已成功解散');
      
      // 解散成功后跳转到首页或团队列表
      window.location.href = '/';
    } catch (error) {
      console.error('解散团队失败:', error);
      window.message?.error(error.message || '解散团队失败，请重试');
    } finally {
      setDeletingTeam(false);
      setDeleteTeamModalVisible(false);
    }
  }, [workspace]);

  // 处理创建团队成功
  const handleCreateTeamSuccess = useCallback((teamData) => {
    // 创建成功后，等待一小段时间确保 Modal 关闭，然后跳转
    setTimeout(() => {
      if (teamData && teamData.id) {
        // 直接跳转到新创建的团队页面
        window.location.href = `/workspace/${teamData.id}`;
      } else {
        // 如果没有具体的团队ID，跳转到当前用户的团队页面
        window.location.href = '/workspace/current';
      }
    }, 500); // 等待500ms确保 Modal 完全关闭
  }, []);

  useEffect(() => {
    async function fetchWorkspace() {
      setLoading(true);
      try {
        // 如果 workspaceId 是 'current'，表示用户没有团队
        if (workspaceId === 'current') {
          setWorkspace(null);
          return;
        }
        const data = await workspaceService.getWorkspaceDetail(workspaceId);
        setWorkspace(data);
      } catch (e) {
        console.error('获取工作区详情失败:', e);
        setWorkspace(null);
      } finally {
        setLoading(false);
      }
    }
    fetchWorkspace();
  }, [workspaceId]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 min-h-screen" style={{ backgroundColor: 'var(--color-bg-chat)' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="max-w-4xl mx-auto py-12 min-h-screen" style={{ backgroundColor: 'var(--color-bg-chat)' }}>
        <EmptyState
          title="暂无团队"
          titleClassName="text-white"
          description="您还没有加入任何团队，可以创建新团队或申请加入现有团队"
          primaryButtonClassName="!text-black"
          actions={{
          primary: '创建团队',
          onPrimary: () => {
            setCreateTeamModalVisible(true);
          }
        }}
        />
        
        {/* 创建团队 Modal - 在空状态时也需要渲染 */}
        <CreateTeamModal
          visible={createTeamModalVisible}
          onCancel={() => setCreateTeamModalVisible(false)}
          onSuccess={handleCreateTeamSuccess}
        />
      </div>
    );
  }

  return (
    <div
      className="w-full mx-auto min-h-screen relative bg-no-repeat bg-cover"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="max-w-4xl mx-auto py-12 min-h-screen relative">
        {/* 面包屑+返回+解散团队 */}
        <div className="mb-6 text-lg font-semibold text-[#18371D] tracking-wide flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              type="text"
              icon={<LeftOutlined />}
              className="!p-0 mr-2 text-base"
              onClick={() => window.history.back()}
            />
            <Breadcrumb
              items={[
                {
                  title: workspace.name
                }
              ]}
            />
          </div>
          
          {/* 解散团队按钮 - 仅队长可见 */}
          {isCaptain && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              className="!p-2 text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={() => setDeleteTeamModalVisible(true)}
            >
              解散团队
            </Button>
          )}
        </div>
        {/* 头像+名称+简介+编辑 */}
        <div className="flex items-center gap-6 mb-8">
          {editMode ? (
            <div className="relative">
              {/* 头像显示 */}
             
              
              {/* 使用 FileUploadList 组件进行头像上传 */}
              <div className="mt-2">
                <FileUploadList
                  fileList={
                    editAvatarFile ? [{
                      uid: 'avatar-upload',
                      name: editAvatarFile.name || 'avatar.jpg',
                      type: editAvatarFile.type || 'image/jpeg',
                      size: editAvatarFile.size || 0,
                      url: editAvatarPreview || editAvatarFile.url,
                      status: 'done'
                    }] : 
                    editAvatar ? [{
                      uid: 'avatar-current',
                      name: 'avatar.jpg',
                      type: 'image/jpeg',
                      size: 0,
                      url: editAvatar,
                      status: 'done'
                    }] : []
                  }
                  onChange={(fileList) => {
                    if (fileList.length > 0) {
                      const file = fileList[0];
                      // 保存文件对象和预览URL，确保包含 id 字段
                      setEditAvatarFile({
                        ...file,
                        id: file.id || file.uid || `avatar_${Date.now()}`,
                        originFileObj: file.originFileObj || file
                      });
                      setEditAvatarPreview(file.url || file.thumbUrl);
                      // 清空现有的头像URL，因为用户选择了新文件
                      setEditAvatar('');
                    } else {
                      // 当文件列表为空时，清空所有头像相关状态
                      setEditAvatarFile(null);
                      setEditAvatarPreview('');
                      setEditAvatar('');
                    }
                  }}
                  accept="image/*"
                  maxCount={1}
                  uploadOptions={{
                    type: 'avatar',
                    workspaceId: workspace?.id
                  }}
                />
              </div>
            </div>
          ) : (
            <Avatar
              size={80}
              src={workspace.avatar}
              className="bg-blue-100 border-2 border-white shadow-lg text-3xl"
              style={{
                width: 80,
                height: 80,
                borderRadius: 20,
                objectFit: 'cover',
              }}
            >
              {workspace.name?.[0] || 'W'}
            </Avatar>
          )}
          <div>
            <div className="flex items-center gap-2 mb-2">
              {editMode ? (
                <Input
                  style={{ width: 200, marginRight: 8 }}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={20}
                  placeholder="工作区名称"
                />
              ) : (
                <span className="text-2xl font-bold text-white">
                  {workspace.name}
                </span>
              )}
              {/* 仅队长可见编辑icon */}
              {!editMode && isCaptain && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  className="!p-0 text-gray-500"
                  onClick={handleEdit}
                />
              )}
            </div>
            <div className="text-white text-sm mb-2">
              {editMode ? (
                <Input.TextArea
                  style={{ width: 260, marginRight: 8 }}
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  maxLength={60}
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  placeholder="工作区简介"
                />
              ) : (
                workspace.description || '暂无简介'
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
             
                <>
                  {/* 组队状态标签 - 显示在最前面 */}
                  {workspace.settings?.recruiting_status && (
                    <Tag
                      className={`rounded-full px-3 py-1 border text-xs ${
                        workspace.settings.recruiting_status === 'recruiting'
                          ? 'bg-green-100 text-green-700 border-green-300'
                          : 'bg-gray-100 text-gray-700 border-gray-300'
                      }`}
                    >
                      {workspace.settings.recruiting_status === 'recruiting' ? '团队招募中' : '团队已锁定'}
                    </Tag>
                  )}
                  
                  {/* 场景标签 */}
                  {workspace.settings?.scene && (
                    <Tag className="rounded-full bg-blue-100 text-blue-700 border-blue-300 px-3 py-1 border text-xs">
                      {workspace.settings.scene}
                    </Tag>
                  )}
                  
                  {/* 缺少角色标签 */}
                  {workspace.settings?.missing_roles &&
                    Array.isArray(workspace.settings.missing_roles) &&
                    workspace.settings.missing_roles.length > 0 &&
                    workspace.settings.missing_roles.map((role, idx) => {
                      const roleLabels = MISSING_ROLES_OPTIONS.reduce((acc, option) => {
                        acc[option.value] = option.label;
                        return acc;
                      }, {});
                      return (
                        <Tag
                          key={idx}
                          className="rounded-full bg-orange-100 text-orange-700 border-orange-300 px-3 py-1 border text-xs"
                        >
                          {roleLabels[role] || role}
                        </Tag>
                      );
                    })}
                  
                  {/* 其他标签 */}
                  {workspace.settings?.tags &&
                    Array.isArray(workspace.settings.tags) &&
                    workspace.settings.tags.length > 0 &&
                    workspace.settings.tags.map((tag, idx) => (
                      <Tag
                        key={idx}
                        className="rounded-full bg-[var(--color-assist-2)] px-3 py-1 border text-xs"
                      >
                        {tag}
                      </Tag>
                    ))}
                  
                  {/* 标签编辑图标 - 仅队长可见，且不在基本信息编辑模式时 */}
                  {isCaptain && !editMode && (
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      size="small"
                      className="!p-1 text-gray-400 hover:text-gray-600"
                      onClick={() => setTagEditModalVisible(true)}
                    />
                  )}
                </>
              
            </div>
            {editMode && (
              <div className="mt-3 flex gap-2">
                <Button
                  type="primary"
                  size="small"
                  loading={saving}
                  onClick={handleSave}
                >
                  保存
                </Button>
                <Button size="small" onClick={() => setEditMode(false)}>
                  取消
                </Button>
              </div>
            )}
          </div>
        </div>
        {/* 统计数据区+分享/申请加入按钮 */}
        <div className="flex gap-8 mb-8 items-center justify-between">
          <div className="flex gap-8">
            <div className="text-center">
              <div className="text-lg font-bold text-white">
                {workspace.member_count || 0}
              </div>
              <div className="text-sm text-gray-500">成员数</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-white">
                {workspace.task_count || 0}
              </div>
              <div className="text-sm text-gray-500">提案数</div>
            </div>
          </div>
          {/* 右侧按钮：队长显示分享，无团队用户显示申请加入 */}
          {isCaptain ? (
            <SharePopover
              message={message}
              userInfo={{
                id: workspace?.id,
                name: workspace?.name,
                bio: workspace?.description,
                tag: workspace?.settings?.tags || [],
                like_count: workspace?.member_count || 0,
                followers: workspace?.member_count || 0,
                following: 0
              }}
              basicUserInfo={{
                name: workspace?.name,
                avatar: workspace?.avatar
              }}
              qrConfig={{
                page: SHARE_QR_CONFIG[SHARE_SCENE_TYPE.WORKSPACE].page,
                getScene: (id) => SHARE_QR_CONFIG[SHARE_SCENE_TYPE.WORKSPACE].getScene(id)
              }}
            >
            <div
              data-property-1="Default"
              className="w-52 px-20 py-1.5 bg-[var(--color-primary)] rounded-[200px] inline-flex flex-col justify-center items-center gap-2.5 cursor-pointer hover:bg-[var(--color-primary-hover)] transition"
            >
              <div className="text-center justify-start text-black text-base font-normal font-['OPPOSans']">
                分享
              </div>
            </div>
            </SharePopover>
          ) : !isSelf && !userHasTeams ? (
            <div
              data-property-1="Default"
              className="w-52 px-10 py-1.5 bg-[var(--color-primary)] rounded-[200px] inline-flex flex-col justify-center items-center gap-2.5 cursor-pointer hover:bg-[var(--color-primary-hover)] transition"
              onClick={() => setApplyModalVisible(true)}
            >
              <div className="text-center justify-start text-black text-base font-normal font-['OPPOSans']">
                申请加入
              </div>
            </div>
          ) : null}
        </div>

        {/* Tab 区 */}
        <div className="mt-8">
          <WorkspaceTab 
            userId={workspace?.id}
            isCaptain={isCaptain}
          />
        </div>
      </div>

      {/* 申请入团 Modal */}
      <ApplyToJoinModal
        visible={applyModalVisible}
        onCancel={() => setApplyModalVisible(false)}
        onOk={handleApplyToJoin}
        workspaceName={workspace?.name}
        workspaceId={workspace?.id}
      />

      {/* 标签编辑 Modal */}
      <TagEditModal
        visible={tagEditModalVisible}
        onCancel={() => setTagEditModalVisible(false)}
        onOk={handleTagEdit}
        initialData={{
          recruiting_status: workspace?.settings?.recruiting_status || 'recruiting',
          scene: workspace?.settings?.scene || '',
          missing_roles: workspace?.settings?.missing_roles || [],
        }}
      />

      {/* 解散团队确认 Modal */}
      <Modal
        open={deleteTeamModalVisible}
        onCancel={() => setDeleteTeamModalVisible(false)}
        onOk={handleDeleteTeam}
        title={
          <div className="flex items-center gap-2">
            <DeleteOutlined className="text-red-500" />
            <span className="text-lg font-medium">解散团队</span>
          </div>
        }
        okText="确认解散"
        cancelText="取消"
        confirmLoading={deletingTeam}
        okButtonProps={{ danger: true }}
        width={500}
        centered
      >
        <div className="py-4">
          <div className="mb-4">
            <div className="text-red-600 font-medium mb-2">⚠️ 警告</div>
            <div className="text-gray-700 mb-4">
              解散团队是不可逆操作，将会：
            </div>
            <ul className="text-gray-600 text-sm space-y-2 mb-4">
              <li>• 永久删除团队及其所有数据</li>
              <li>• 移除所有团队成员关系</li>
              <li>• 清理所有团队邀请和申请</li>
              <li>• 删除所有团队提案和内容</li>
            </ul>
            <div className="text-gray-700">
              确定要解散团队 <span className="font-medium text-gray-900">"{workspace?.name}"</span> 吗？
            </div>
          </div>
        </div>
      </Modal>

      {/* 创建团队 Modal */}
      <CreateTeamModal
        visible={createTeamModalVisible}
        onCancel={() => setCreateTeamModalVisible(false)}
        onSuccess={handleCreateTeamSuccess}
      />
    </div>
  );
};

export default WorkspacePage;
